
import { ConferenceListItem } from "@gsx/common/src/types/http/dashboard/conference";
import { Container } from "inversify";
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { focusOnFirstInvalidInput } from "../../../../common/src/form/utils";
import { AuthStore } from "../../../../common/src/stores/AuthStore";
import { ConferenceDestroyStore } from "../../stores/conference/ConferenceDestroyStore";
import { ConferenceEditStore } from "../../stores/conference/ConferenceEditStore";
import { ConferenceListStore } from "../../stores/conference/ConferenceListStore";
import { UserEditFavoriteStore } from "../../stores/user/UserEditFavoriteStore";
import EditEventModal from "../conference/EditEventModal.vue";
import ViewEventModal from "../conference/ViewEventModal.vue";
import { isAdmin } from "@gsx/common";
import FavoriteBtn from "../components/FavoriteBtn.vue";
import { OrganizationListStore } from "../../stores/organization/OrganizationListStore";

@Component({
    components: { EditEventModal, ViewEventModal, FavoriteBtn },
})
export default class GlobalCalendarCard extends Vue {
    @Prop() conferenceListItem!: ConferenceListItem;
    @Inject("container") readonly container!: Container;
    listStore: ConferenceListStore = this.container.get(ConferenceListStore);
    editStore: ConferenceEditStore = this.container.get(ConferenceEditStore);
    destroyStore: ConferenceDestroyStore = this.container.get(ConferenceDestroyStore);
    authStore: AuthStore = this.container.get(AuthStore);
    userEditFavoriteStore: UserEditFavoriteStore = this.container.get(UserEditFavoriteStore);
    isCreateModalVisible = false;
    isEditModalVisible = false;
    isViewModalVisible = false;
    organizationListStore: OrganizationListStore = this.container.get(OrganizationListStore);

    organizationUrl(organizationName: string) {
        const foundOrg = this.organizationListStore.items?.find(
            (org) => org.organizationName === organizationName,
        );

        if (foundOrg) {
            return `/dashboard/organizations/${foundOrg.organizationid}`;
        } else {
            return null;
        }
    }

    addToFavorites() {
        if (
            !this.authStore.user?.favorites?.conferenceIds?.includes(
                this.conferenceListItem.conferenceid,
            )
        ) {
            this.authStore.user?.favorites?.conferenceIds?.push(
                this.conferenceListItem.conferenceid,
            );
            this.userEditFavoriteStore.addConference(this.conferenceListItem.conferenceid);
        }
    }

    removeEvent() {
        const indexOf =
            this.authStore.user!.favorites!.conferenceIds?.indexOf(
                this.conferenceListItem.conferenceid,
            ) || -1;
        if (indexOf >= 0) {
            this.authStore.user?.favorites?.conferenceIds?.splice(indexOf, 1);
        }
        this.userEditFavoriteStore.removeConference(this.conferenceListItem.conferenceid);
    }

    toggleFavorite() {
        if (!this.isFavorited) {
            this.authStore.user?.favorites?.conferenceIds?.push(
                this.conferenceListItem.conferenceid,
            );
            this.userEditFavoriteStore.addConference(this.conferenceListItem.conferenceid);
        } else {
            const indexOf =
                this.authStore.user!.favorites!.conferenceIds?.indexOf(
                    this.conferenceListItem.conferenceid,
                ) ?? -1;
            if (indexOf >= 0) {
                this.authStore.user?.favorites?.conferenceIds?.splice(indexOf, 1);
            }
            this.userEditFavoriteStore.removeConference(this.conferenceListItem.conferenceid);
        }
    }

    get isFavorited() {
        return this.authStore.user?.favorites?.conferenceIds?.includes(
            this.conferenceListItem.conferenceid,
        );
    }

    displayEditModal() {
        this.isEditModalVisible = true;
    }

    displayViewModal() {
        this.isViewModalVisible = true;
    }

    hideEditModal() {
        this.isEditModalVisible = false;
    }

    hideViewModal() {
        this.isViewModalVisible = false;
    }

    async editEvent() {
        await this.editStore.submit();

        if (this.editStore.failed) {
            focusOnFirstInvalidInput();
        } else {
            this.hideEditModal();
            await this.listStore.reload();
        }
    }

    async destroyEvent() {
        await this.destroyStore.delete(this.editStore.conference!.conferenceid);

        if (!this.destroyStore.failed) {
            this.hideEditModal();
            await this.listStore.reload();
        }
    }

    handleEventClick() {
        this.openEventModal(this.conferenceListItem);
    }

    get canManageEvent(): boolean {
        return (
            isAdmin(this.authStore.user) ||
            this.authStore.user!.userid === this.editStore.conference!.userId
        );
    }

    openEventModal(conference: ConferenceListItem) {
        this.editStore.setConference(conference);

        if (this.canManageEvent) {
            this.displayEditModal();
        } else {
            this.displayViewModal();
        }
    }
}
